import {InputText} from '@/components/commons/input-text/InputText'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import i18n, {useTranslation} from '@/translations/i18n'
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {useChangePassword} from '@/features/account/services/useChangePassword'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useAccountStore} from '../../store/useAccountStore'
import {toast} from '@/utilities/toast/toast'

const ChangePasswordValidationSchema = z
    .object({
        currentPassword: z.string(),
        password: z
            .string()
            .min(8, i18n.t('commons:password_rules'))
            .regex(/\d/, {message: i18n.t('commons:password_rules')}),
        confirmPassword: z.string()
    })
    .superRefine(({password, confirmPassword}, ctx) => {
        if (confirmPassword != password) {
            ctx.addIssue({
                code: 'custom',
                message: i18n.t('errors:confirm_password'),
                path: ['confirmPassword']
            })
        }
    })
type ChangePasswordValidationSchema = z.infer<typeof ChangePasswordValidationSchema>

export const ChangePasswordForm = () => {
    const {t} = useTranslation()
    const form = useForm<ChangePasswordValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(ChangePasswordValidationSchema)
    })

    const setEditingSection = useAccountStore(state => state.setEditingSection)

    // form values
    const currentPassword = useWatch({control: form.control, name: 'currentPassword'})
    const password = useWatch({control: form.control, name: 'password'})
    const confirmPassword = useWatch({control: form.control, name: 'confirmPassword'})

    // mutation
    const changePasswordMutation = useChangePassword({
        onSuccess: () => {
            toast.success(t('account:password_changed'))
            setEditingSection(null)
        },
        onError: error => {
            console.error(error)
            if (error.response?.data.code == 'password-mismatch') {
                form.setError('currentPassword', {message: t('account:current_password_not_valid')})
                form.setValue('password', '')
                form.setValue('confirmPassword', '')
                toast.error(t('account:current_password_not_valid'))
            }
        }
    })

    // form submission
    const handleSubmit = form.handleSubmit(() =>
        changePasswordMutation.mutate({
            password,
            passwordConfirmation: confirmPassword,
            currentPassword: currentPassword
        })
    )

    return (
        <Flexbox render="form" onSubmit={handleSubmit} direction="column" align="flex-end" gap={7} fullWidth>
            <Flexbox direction="column" gap={3} fullWidth>
                <InputText
                    type="password"
                    label={t('account:current_password')}
                    placeholder={'••••••••••'}
                    errorMessage={form.formState.errors.currentPassword?.message}
                    helpText={t('account:current_password_hint')}
                    visibilityToggle
                    {...form.register('currentPassword')}
                />
                <InputText
                    type="password"
                    label={t('account:new_password')}
                    placeholder={'••••••••••'}
                    errorMessage={form.formState.errors.password?.message}
                    helpText={t('commons:password_rules')}
                    visibilityToggle
                    {...form.register('password')}
                />
                <InputText
                    type="password"
                    label={t('account:repeat_new_password')}
                    placeholder={'••••••••••'}
                    visibilityToggle
                    touched={form.formState.isDirty}
                    errorMessage={form.formState.errors.confirmPassword?.message}
                    successMessage={
                        form.formState.isValid && password == confirmPassword ? t('commons:password_match') : ''
                    }
                    {...form.register('confirmPassword')}
                />
            </Flexbox>
            <Button disabled={changePasswordMutation.isPending} type="submit" variant="primaryNeutral">
                {t('account:change_password')}
                {changePasswordMutation.isPending && <Spinner />}
            </Button>
        </Flexbox>
    )
}
